import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn } from '../components/Btn.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { Field, TextInput } from '../components/Field.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '../lib/branding.tsx';

export function AdminLogin() {
  const { user, signInWithPassword } = useAdminAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const brandName = branding.companyName?.replace(/ Safety.*$/i, '') ?? 'Locate2u';

  useEffect(() => {
    if (user) navigate('/admin', { replace: true });
  }, [user, navigate]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signInWithPassword(email.trim(), password);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 text-center">
          {branding.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={brandName}
              className="mx-auto mb-3 h-10 w-auto"
            />
          )}
          <div
            className="brand-wordmark mb-1 text-2xl"
            style={{ color: 'var(--brand-primary)' }}
            aria-label="Safety & Compliance"
          >
            Safety<span className="accent">&amp;</span>Compliance
          </div>
          <div className="space-y-0.5 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <div>Powered by {brandName}</div>
            <div>Powered by Locate2u</div>
          </div>
          <p className="mt-4 text-sm text-slate-500">Sign in to the admin portal</p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <Field label="Email" required>
            <TextInput
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field label="Password" required>
            <TextInput
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>
          <ErrorMessage error={error} />
          <Btn
            type="submit"
            loading={submitting}
            disabled={!email.trim() || !password}
            className="w-full"
          >
            Sign in
          </Btn>
        </form>
      </div>
    </div>
  );
}
